import { TreeNode } from './Tree';

/**
 * Eine flache Ablageliste in den Baum, den der `Tree` zeichnet.
 *
 * Der Speicher liefert Ordner und Dateien nebeneinander, jeweils mit ihrem vollen Pfad; die
 * Verschachtelung steht nur in diesen Pfaden. Sie hier einmal herzustellen — und nicht im
 * Container, der sie braucht — ist der Grund für dieses Modul: Der Dateimanager und die
 * Datenraum-Ansicht zeichnen denselben Baum über denselben Ablagen, und zwei Fassungen dieser
 * Zusammensetzung wären zwei Antworten auf die Frage, wo eine Datei liegt.
 *
 * Wie ein Knoten aussieht, entscheidet der Aufrufer über `createFile`/`createFolder`. Das ist die
 * Naht, an der die beiden Ansichten sich unterscheiden dürfen: Der Dateimanager hängt
 * Wissensbasis-Angaben an, der Datenraum den Zustand der Analyse — die Verschachtelung ist
 * dieselbe.
 */

/** Was der Speicher je Eintrag liefert; alles Weitere ist Sache des Aufrufers. */
export interface StorageItem {
    path: string;
    isDirectory?: boolean;
    [key: string]: any;
}

export interface TreeBuilders {
    createFile: (item: StorageItem) => TreeNode;
    createFolder: (path: string, name: string, files: TreeNode[]) => TreeNode;
}

const SLASH = 47; // '/'

/** Der letzte nicht leere Abschnitt eines Pfades. */
export const nameOfPath = (path: string): string => {
    const text = String(path ?? '');

    let end = text.length;
    while (end > 0 && text.charCodeAt(end - 1) === SLASH) end--;
    if (end === 0) return '';

    return text.slice(text.lastIndexOf('/', end - 1) + 1, end);
};

/**
 * Der Ordnerpfad, in dem ein Pfad liegt — mit Schrägstrich am Ende, wie der Speicher Ordner
 * benennt. Für alles direkt an der Wurzel ist das `/`.
 */
export const parentOfPath = (path: string): string => {
    const text = String(path ?? '');

    // Leere Abschnitte (`//`) zählen nicht mit; der seltene Fall bleibt auf dem langsamen Weg.
    if (text.includes('//')) {
        const parts = text.split('/').filter((part) => part !== '');

        return parts.length > 1 ? `/${parts.slice(0, -1).join('/')}/` : '/';
    }

    let end = text.length;
    while (end > 0 && text.charCodeAt(end - 1) === SLASH) end--;

    const cut = text.lastIndexOf('/', end - 1);
    if (cut <= 0) return '/';

    return `${text.charCodeAt(0) === SLASH ? '' : '/'}${text.slice(0, cut)}/`;
};

export const buildStorageTree = (items: StorageItem[], builders: TreeBuilders): TreeNode[] => {
    const folders = new Map<string, TreeNode>();

    // Erst alle Ordner: Ein Kind kann nur einsortiert werden, wo sein Ordner schon existiert.
    (items ?? [])
        .filter((item) => item?.isDirectory)
        .forEach((item) => {
            folders.set(item.path, builders.createFolder(item.path, nameOfPath(item.path) || 'Unknown', []));
        });

    const roots: TreeNode[] = [];

    /**
     * In den Ordner, sonst an die Wurzel.
     *
     * Ein Eintrag, dessen Ordner die Liste gar nicht enthält, landet oben statt zu verschwinden.
     * Das ist bewusst nachsichtig: Eine unvollständige Auflistung — ein Ordner, den ein Filter
     * weggelassen hat — soll den Baum lichten, nicht Dateien unsichtbar machen.
     */
    const place = (node: TreeNode, path: string) => {
        const parent = folders.get(parentOfPath(path));

        if (parent?.files) parent.files.push(node);
        else roots.push(node);
    };

    (items ?? [])
        .filter((item) => item && !item.isDirectory)
        .forEach((item) => place(builders.createFile(item), item.path));

    // Ordner zuletzt und über den Pfad sortiert, damit ein Ordner immer schon eingehängt ist,
    // bevor seine Unterordner ihn suchen.
    Array.from(folders.keys())
        .sort()
        .forEach((path) => place(folders.get(path) as TreeNode, path));

    return roots;
};
